// Copyright 2024 Sorama B.V.
import { GenericMeasurement, Point3D } from "datatypes/SoramaWebApi";
import { UnitSpectrumWithMeasurement } from "datatypes/WeightedSpectrumWithMeasurement";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { IPsychoAcousticsProcessingService } from "services/SoundProcessing/IPsychoAcousticsProcessingService";
import { PsychoAcousticUnits } from "units/PsychoAcousticsUnits";
import { PsychoAcousticBandUnits, PsychoAcousticMetricType } from "../PsychoAcoustics/SpiderChart/SpiderChart";
import { ISpectrumProvider } from "./ISpectrumProvider";
import { ComputationMode } from "./SpectrumModuleModes";

export class PsychoAcousticsSpectrumProvider implements ISpectrumProvider {
    constructor(
        private readonly processorResolver: IMeasurementProcessorResolver,
        private readonly metricType: PsychoAcousticMetricType
    ) {}

    public async getSpectra(
        measurement: GenericMeasurement,
        mic: Point3D,
        computationMode: ComputationMode,
        progressCallback: (progress: number) => void
    ): Promise<UnitSpectrumWithMeasurement> {
        const processor = await this.processorResolver.resolveProcessor<IPsychoAcousticsProcessingService>(
            IPsychoAcousticsProcessingService,
            measurement
        );

        const result = await processor.getPsychoAcousticMetricBands(
            measurement,
            {
                requestedMicrophone: mic,
                metricName: this.metricType,
            },
            progressCallback
        );

        const frequencySamplePairs = result.data;

        const xUnit = PsychoAcousticBandUnits[this.metricType];
        const yUnit = PsychoAcousticUnits.fromString(this.metricType);
        const timeRange = {
            from: 0,
            to: measurement.duration,
        };

        const unitSpectrumWithMeasurement = new UnitSpectrumWithMeasurement(
            xUnit,
            yUnit,
            frequencySamplePairs,
            timeRange
        );
        unitSpectrumWithMeasurement.measurement = measurement;
        unitSpectrumWithMeasurement.usedComputationMode = computationMode;

        return unitSpectrumWithMeasurement;
    }
}
